/**
 * Crowd Count SRS overlay: seat squares + face → Section/Row/Seat.
 *
 * buildOverlay(map, tiles, scan, rowDict) → { byKey, seats }
 * roundToSrs(tile, bbox, data)            → detail rows | null
 *
 * Seat pan/tilt MUST use Mapping/acsSeatMath (ACS clone — DO NOT CHANGE).
 */
import { seatsFromMap } from '../../Mapping/seatsFromMap';
import {
  colorForLabel, scanCellPtz, TILE_CSS_H, TILE_CSS_W, tileBoxesKey,
} from '../helpers';
import {
  regionPitch,
  seatSquareStyleTile,
} from '../../../shared/srsSeatSquare';

const EMPTY = { byKey: {}, byScan: {}, seats: [] };
const FALLBACK_SIZE = 0.08 * Math.min(TILE_CSS_W, TILE_CSS_H);
const TILE_RC_PATTERN = /_r(\d+)_c(\d+)/i;

const num = (v) => {
  const n = Number(v);
  return v == null || v === '' || !Number.isFinite(n) ? null : n;
};
const avg = (xs) => (xs.length ? xs.reduce((s, n) => s + n, 0) / xs.length : 0);

/** pan/tilt → per-tile mosaic coords (scanBounds steps, else endpoint fit). */
const placeOnMosaic = (seats, tiles, scan) => {
  if (!seats?.length || !tiles?.length || !scan?._id) return [];

  const b = scan.scanBounds || {};
  const aPan = num(b.start?.pan);
  const aTilt = num(b.start?.tilt);
  const panStep = num(b.panStep ?? scan.panStep);
  const tiltStep = num(b.tiltStep ?? scan.tiltStep);
  const useSteps = aPan != null && aTilt != null && panStep && tiltStep;

  const byRc = new Map();
  const panAt = new Map();
  const tiltAt = new Map();
  let maxRow = 0;
  let maxCol = 0;

  tiles.forEach((tile) => {
    const name = `${tile?.key || ''} ${tile?.url || ''} ${tile?.webp || ''}`;
    const rc = TILE_RC_PATTERN.exec(name);
    const row = rc ? Number(rc[1]) : num(tile.row);
    const col = rc ? Number(rc[2]) : num(tile.col);
    if (row == null || col == null) return;
    maxRow = Math.max(maxRow, row);
    maxCol = Math.max(maxCol, col);
    byRc.set(`${row}-${col}`, tileBoxesKey(tile));

    const cell = scanCellPtz(scan, col, row);
    if (cell.pan != null) panAt.set(col, cell.pan);
    if (cell.tilt != null) tiltAt.set(row, cell.tilt);
  });

  const rowsN = maxRow + 1;
  const colsN = maxCol + 1;
  if (rowsN <= 0 || colsN <= 0 || !panAt.size || !tiltAt.size) return [];

  const pans = [...panAt.entries()].sort((a, c) => a[0] - c[0]);
  const tilts = [...tiltAt.entries()].sort((a, c) => a[0] - c[0]);
  const fit = (value, samples) => {
    if (samples.length < 2) return samples[0]?.[0] ?? null;
    const [i0, v0] = samples[0];
    const [i1, v1] = samples[samples.length - 1];
    return v1 === v0 ? i0 : i0 + ((value - v0) / (v1 - v0)) * (i1 - i0);
  };

  const scanId = String(scan._id);
  return seats.flatMap((seat) => {
    const colF = useSteps ? (seat.pan - aPan) / panStep : fit(seat.pan, pans);
    const rowF = useSteps ? (seat.tilt - aTilt) / tiltStep : fit(seat.tilt, tilts);
    if (!Number.isFinite(colF) || !Number.isFinite(rowF)) return [];

    const mx = (colF + 0.5) / colsN;
    const my = (rowF + 0.5) / rowsN;
    if (mx < 0 || mx > 1 || my < 0 || my > 1) return [];

    const col = Math.min(colsN - 1, Math.max(0, Math.floor(mx * colsN - 1e-9)));
    const row = Math.min(rowsN - 1, Math.max(0, Math.floor(my * rowsN - 1e-9)));
    const tileKey = byRc.get(`${row}-${col}`);
    if (!tileKey) return [];

    const x = mx * colsN - col;
    const y = my * rowsN - row;
    return [{
      id: `${scanId}-${seat.section}-${seat.row}-${seat.seat}-${tileKey}`,
      label: `${seat.section || '—'} - ${seat.row || '—'} - ${seat.seat ?? '—'}`,
      section: seat.section,
      regionKey: seat.regionKey,
      row: seat.row,
      seat: Number(seat.seat),
      scanId,
      tileKey,
      x,
      y,
      xPx: (col + x) * TILE_CSS_W,
      yPx: (row + y) * TILE_CSS_H,
    }];
  });
};

/** Avg consecutive-seat pitch in a region → edge-to-edge square size. */
const regionPitchTile = (seats) => regionPitch(seats, {
  xKey: 'xPx',
  yKey: 'yPx',
  rowKey: 'row',
  seatKey: 'seat',
});

/** Clamp square fully inside its tile (flush to edge if needed). */
const fitSeatInTile = (s) => {
  const side = Math.min(s.sizePx || FALLBACK_SIZE, TILE_CSS_W, TILE_CSS_H);
  const halfW = side / (2 * TILE_CSS_W);
  const halfH = side / (2 * TILE_CSS_H);
  const x = Math.min(1 - halfW, Math.max(halfW, s.x));
  const y = Math.min(1 - halfH, Math.max(halfH, s.y));
  const col = Math.floor(s.xPx / TILE_CSS_W + 1e-9);
  const row = Math.floor(s.yPx / TILE_CSS_H + 1e-9);
  return {
    ...s,
    sizePx: side,
    x,
    y,
    xPx: (col + x) * TILE_CSS_W,
    yPx: (row + y) * TILE_CSS_H,
  };
};

const withRegionSquares = (placed) => {
  if (!placed.length) return EMPTY;

  const groups = new Map();
  placed.forEach((s) => {
    const key = s.regionKey || s.section || '?';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  });

  const sizeByRegion = new Map();
  const known = [];
  groups.forEach((list, key) => {
    const pitch = regionPitchTile(list);
    sizeByRegion.set(key, pitch);
    if (pitch) known.push(pitch);
  });
  const fallback = known.length ? avg(known) : FALLBACK_SIZE;

  const byKey = {};
  const byScan = {};
  const seats = [];
  groups.forEach((list, key) => {
    const sizePx = sizeByRegion.get(key) || fallback;
    list.forEach((s) => {
      const seat = fitSeatInTile({ ...s, sizePx });
      if (!byKey[seat.tileKey]) byKey[seat.tileKey] = [];
      byKey[seat.tileKey].push(seat);
      if (seat.scanId) {
        if (!byScan[seat.scanId]) byScan[seat.scanId] = [];
        byScan[seat.scanId].push(seat);
      }
      seats.push(seat);
    });
  });
  return { byKey, byScan, seats };
};

const buildOverlay = (camMap, tiles, scan, rowDict) => (
  withRegionSquares(placeOnMosaic(seatsFromMap(camMap, rowDict), tiles, scan))
);

const mergeOverlays = (parts) => {
  const seats = (parts || []).flatMap((p) => p?.seats || []);
  if (!seats.length) return EMPTY;
  const byKey = {};
  const byScan = {};
  seats.forEach((s) => {
    if (!byKey[s.tileKey]) byKey[s.tileKey] = [];
    byKey[s.tileKey].push(s);
    if (s.scanId) {
      if (!byScan[s.scanId]) byScan[s.scanId] = [];
      byScan[s.scanId].push(s);
    }
  });
  return { byKey, byScan, seats };
};

/** Prefer seats on this tile (O(tile)), then this scan, then the full overlay. */
const seatsForTile = (tile, data) => {
  if (!data) return [];
  const local = data.byKey?.[tileBoxesKey(tile)];
  if (local?.length) return local;
  const scanId = tile?._scanId;
  if (scanId && data.byScan?.[scanId]?.length) return data.byScan[scanId];
  return data.seats || [];
};

const seatSquareStyle = (s) => seatSquareStyleTile(
  s,
  TILE_CSS_W,
  TILE_CSS_H,
  colorForLabel(s.section || '?'),
);

/** Section always (nearest); Row/Seat only if face center is inside a seat square. */
const roundToSrs = (tile, bbox, data) => {
  if (!tile || !bbox || !data?.seats?.length) return null;
  const x = ((Number(tile.col) || 0) + (Number(bbox.Left) || 0) + (Number(bbox.Width) || 0) / 2)
    * TILE_CSS_W;
  const y = ((Number(tile.row) || 0) + (Number(bbox.Top) || 0) + (Number(bbox.Height) || 0) / 2)
    * TILE_CSS_H;
  const scanId = tile._scanId;

  let onSquare = null;
  let nearest = null;
  let nearestD = Infinity;
  let squareD = Infinity;

  seatsForTile(tile, data).forEach((s) => {
    if (scanId && s.scanId && scanId !== s.scanId) return;
    const d = (s.xPx - x) ** 2 + (s.yPx - y) ** 2;
    if (d < nearestD) {
      nearestD = d;
      nearest = s;
    }
    const half = (s.sizePx || FALLBACK_SIZE) / 2;
    if (Math.abs(s.xPx - x) <= half && Math.abs(s.yPx - y) <= half && d < squareD) {
      squareD = d;
      onSquare = s;
    }
  });

  const hit = onSquare || nearest;
  if (!hit) return null;

  const rows = [['Section', hit.section || '—']];
  if (onSquare) {
    rows.push(['Row', onSquare.row || '—'], ['Seat', String(onSquare.seat ?? '—')]);
  }
  return rows;
};

/**
 * Atlas-pixel hit test for a pre-baked seat (CSS mosaic xPx/yPx → atlas).
 * Returns the nearest seat under the point, or null.
 */
const pickSeatAt = ({
  overlay, scanId = null, x, y, atlasW, atlasH, cols, rows,
}) => {
  const seats = (scanId && overlay?.byScan?.[scanId]) || overlay?.seats;
  const c = Number(cols) || 0;
  const r = Number(rows) || 0;
  if (!seats?.length || atlasW <= 0 || atlasH <= 0 || c <= 0 || r <= 0) return null;

  const cellW = atlasW / c;
  const cellH = atlasH / r;
  const xCss = x * (TILE_CSS_W / cellW);
  const yCss = y * (TILE_CSS_H / cellH);

  let best = null;
  let bestD = Infinity;
  seats.forEach((s) => {
    if (scanId && s.scanId && s.scanId !== scanId) return;
    const half = (s.sizePx || FALLBACK_SIZE) / 2;
    if (Math.abs(s.xPx - xCss) <= half && Math.abs(s.yPx - yCss) <= half) {
      const d = (s.xPx - xCss) ** 2 + (s.yPx - yCss) ** 2;
      if (d < bestD) {
        bestD = d;
        best = s;
      }
    }
  });
  return best;
};

export {
  EMPTY,
  buildOverlay,
  mergeOverlays,
  roundToSrs,
  seatSquareStyle,
  pickSeatAt,
  seatsForTile,
};
